import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Printer, ShieldCheck, FileText } from "lucide-react";

function Metric({ label, value, suffix }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-center">
      <p className="font-display text-2xl font-bold text-[#1e3a8a]">
        {value}
        {suffix}
      </p>
      <p className="mt-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</p>
    </div>
  );
}

export default function ReportModal({ report, open, onOpenChange }) {
  if (!report) return null;
  const s = report.stats || {};
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="report-modal">
        <div className="print-area">
          <div className="flex items-start justify-between border-b border-slate-200 pb-4">
            <div>
              <div className="flex items-center gap-2 text-[#1e3a8a]">
                <FileText className="h-5 w-5" />
                <h2 className="font-display text-xl font-bold">Quality of Visit Report</h2>
              </div>
              <p className="mt-1 text-sm text-slate-600">
                {report.parent_name}
                {report.child_name ? ` · Child: ${report.child_name}` : ""}
              </p>
              <p className="text-xs text-slate-400">
                Generated by {report.generated_by} · {new Date(report.created_at).toLocaleString()}
              </p>
            </div>
            <div className="flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700">
              <ShieldCheck className="h-3.5 w-3.5" /> Verified
            </div>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-6">
            <Metric label="Total" value={s.total ?? 0} />
            <Metric label="Completed" value={s.completed ?? 0} />
            <Metric label="Approval" value={s.approval_rate ?? 0} suffix="%" />
            <Metric label="Punctuality" value={s.punctuality_index ?? 0} suffix="%" />
            <Metric label="Late" value={s.late ?? 0} />
            <Metric label="Engagement" value={s.avg_engagement ?? 0} suffix="/5" />
          </div>

          <div className="mt-5 whitespace-pre-wrap rounded-lg border border-slate-200 bg-white p-4 text-sm leading-relaxed text-slate-700">
            {report.content}
          </div>

          {report.visits && report.visits.length > 0 && (
            <div className="mt-5">
              <h3 className="font-display text-sm font-bold uppercase tracking-wide text-slate-700">
                Documented Visit Log
              </h3>
              <div className="mt-2 overflow-hidden rounded-lg border border-slate-200">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Date</th>
                      <th className="px-3 py-2 font-semibold">Time</th>
                      <th className="px-3 py-2 font-semibold">Status</th>
                      <th className="px-3 py-2 font-semibold">Punctuality</th>
                      <th className="px-3 py-2 font-semibold">Eng.</th>
                      <th className="px-3 py-2 font-semibold">Safety</th>
                      <th className="px-3 py-2 font-semibold">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {report.visits.map((v, i) => {
                      const a = v.assessment || {};
                      const punc = a.punctuality || v.timeliness;
                      const noShow = punc === "no_show";
                      const late = punc === "late";
                      return (
                        <tr key={i} className="text-slate-700">
                          <td className="px-3 py-2">{v.visit_date}</td>
                          <td className="px-3 py-2">{v.start_time}–{v.end_time}</td>
                          <td className="px-3 py-2 capitalize">{v.status.replace("_", " ")}</td>
                          <td className="px-3 py-2">
                            {punc && punc !== "not_recorded" ? (
                              <span className={`rounded px-1.5 py-0.5 font-medium ${noShow ? "bg-red-50 text-red-700" : late ? "bg-orange-50 text-orange-700" : "bg-emerald-50 text-emerald-700"}`}>
                                {noShow ? "No Show" : late ? "Not On Time" : "On Time"}
                              </span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2">{a.engagement ? `${a.engagement}/5` : "—"}</td>
                          <td className="px-3 py-2">{a.emotional_safety ? `${a.emotional_safety}/5` : "—"}</td>
                          <td className="px-3 py-2 text-slate-500">{a.observation_note || v.no_show_reason || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="mt-6 flex items-end justify-between gap-6 border-t border-slate-200 pt-6">
            <div className="flex-1">
              <div className="h-8 border-b border-slate-400" />
              <p className="mt-1 text-sm font-semibold text-slate-800">{report.generated_by}</p>
              <p className="text-[11px] uppercase tracking-wide text-slate-400">Social Worker Signature</p>
            </div>
            <div className="w-40">
              <div className="h-8 border-b border-slate-400 pb-1 text-center font-mono text-sm text-slate-700">
                {new Date(report.created_at).toLocaleDateString()}
              </div>
              <p className="mt-1 text-[11px] uppercase tracking-wide text-slate-400">Date</p>
            </div>
          </div>

          <p className="mt-4 font-mono text-[10px] text-slate-400">
            AUDIT-HASH: {report.id}
          </p>
        </div>

        <div className="no-print mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="close-report-btn">
            Close
          </Button>
          <Button onClick={() => window.print()} data-testid="print-report-btn" className="bg-[#0d9488] hover:bg-[#115e59]">
            <Printer className="mr-2 h-4 w-4" /> Print / Save PDF
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
